import { TabContext, TabPanel } from '@mui/lab';
import { Autocomplete, Box, Paper, Tab, Tabs, TextField } from '@mui/material';
import { addMonths, endOfDay, startOfDay } from 'date-fns';
import _ from 'lodash';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch } from 'react-redux';

import AppNav from '@sophocles/components/App/AppNav';
import TitleBar from '@sophocles/components/App/TitleBar';
import TimeRangeSelector from '@sophocles/components/TimeRangeSelector';
import { fetchJobConfigurationsWithFilters } from '@sophocles/entities/JobConfiguration/actions';
import CrewBillingTable from '@sophocles/views/services/components/Billing/CrewBillingTable';
import DateBillingTable from '@sophocles/views/services/components/Billing/DateBillingTable';
import JobDetailTable from '@sophocles/views/services/components/Billing/JobDetailTable';
import ServiceBillingTable from '@sophocles/views/services/components/Billing/ServiceBillingTable';

function ReportingView(props) {
  const dispatch = useDispatch();
  const { t } = useTranslation();

  const [tabValue, setTabValue] = useState('0');
  const [startDate, setStartDate] = useState(() => startOfDay(addMonths(new Date(), -1)));
  const [endDate, setEndDate] = useState(() => endOfDay(new Date()));

  const [customers, setCustomers] = useState([]);
  const [selectedCustomer, setSelectedCustomer] = useState(null);

  useEffect(() => {
    dispatch(fetchJobConfigurationsWithFilters({ limit: 200, offset: 0 })).then(data => {
      if (data && !data.error) {
        const configs = Array.isArray(data) ? data : [];
        const customerList = configs
          .map(config => ({
            id: _.get(config, ['customer', 'id']),
            name: _.get(config, ['customer', 'name'], 'Unknown'),
          }))
          .filter(c => c.id);
        setCustomers(customerList);
      }
    });
  }, [dispatch]);

  const onTabChange = (event, newValue) => setTabValue(newValue);

  const handleDateRangeChange = useCallback((start, end) => {
    setStartDate(start);
    setEndDate(end);
  }, []);

  const extraParams = useMemo(() => {
    if (!selectedCustomer) return undefined;
    return { site__primary_customer: selectedCustomer.id };
  }, [selectedCustomer]);

  const toolbarFilters = (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
      <Autocomplete
        options={customers}
        getOptionLabel={option => option.name}
        value={selectedCustomer}
        onChange={(e, value) => setSelectedCustomer(value)}
        isOptionEqualToValue={(option, value) => option.id === value.id}
        renderInput={params => <TextField {...params} label={t('general.customer')} size="small" />}
        sx={{ minWidth: 260 }}
      />
      <TimeRangeSelector
        startDate={startDate}
        endDate={endDate}
        onChange={handleDateRangeChange}
        size="sm"
        exclude={['last1h', 'last24h']}
        defaultPreset="last30d"
      />
    </Box>
  );

  return (
    <AppNav page="services_reporting" notFullHeight {...props}>
      <Box sx={{ top: '64px', mx: 2, pb: '24px' }}>
        <TitleBar showBackButton title={t('projectServices.reporting.pageTitle')} />
        <Paper sx={{ mx: 1 }}>
          <TabContext value={tabValue}>
            <Tabs variant="fullWidth" value={tabValue} onChange={onTabChange}>
              <Tab label={t('projectServices.reporting.tabs.dateSummary')} value="0" />
              <Tab label={t('projectServices.reporting.tabs.serviceSummary')} value="1" />
              <Tab label={t('projectServices.reporting.tabs.crewSummary')} value="2" />
              <Tab label={t('projectServices.reporting.tabs.jobDetail')} value="3" />
            </Tabs>
            <TabPanel value="0">
              <DateBillingTable
                startDate={startDate}
                endDate={endDate}
                extraParams={extraParams}
                extraToolbarContent={toolbarFilters}
              />
            </TabPanel>
            <TabPanel value="1">
              <ServiceBillingTable
                startDate={startDate}
                endDate={endDate}
                extraParams={extraParams}
                extraToolbarContent={toolbarFilters}
              />
            </TabPanel>
            <TabPanel value="2">
              <CrewBillingTable
                startDate={startDate}
                endDate={endDate}
                extraParams={extraParams}
                extraToolbarContent={toolbarFilters}
              />
            </TabPanel>
            <TabPanel value="3">
              <JobDetailTable
                startDate={startDate}
                endDate={endDate}
                extraParams={extraParams}
                extraToolbarContent={toolbarFilters}
              />
            </TabPanel>
            <TabPanel value="2">
              <CrewBillingTable
                startDate={startDate}
                endDate={endDate}
                extraParams={extraParams}
                extraToolbarContent={toolbarFilters}
              />
            </TabPanel>
            <TabPanel value="3">
              <JobDetailTable
                startDate={startDate}
                endDate={endDate}
                extraParams={extraParams}
                extraToolbarContent={toolbarFilters}
              />
            </TabPanel>
          </TabContext>
        </Paper>
      </Box>
    </AppNav>
  );
}

export default ReportingView;
